import { FabricDetector } from './FabricDetector';

/**
 * MVP fabric region estimate:
 * 1) Background from a **border ring** (not just corners — avoids fabric in corners).
 * 2) **Coverage** = fraction of pixels classified as foreground (matches “how much of the frame is fabric”).
 * 3) **BBox for edge checks** = 6th–94th percentile of fg coordinates (ignores speckles; stable edges).
 * 4) If the mask is degenerate (~whole frame fg), fall back to a neutral center estimate (low confidence).
 *
 * @extends FabricDetector
 */
export class HeuristicCornerBackgroundFabricDetector extends FabricDetector {
  /**
   * @param {{ colorDistanceThreshold?: number, minForegroundRatio?: number }} [opts]
   */
  constructor(opts = {}) {
    super();
    this.colorDistanceThreshold = opts.colorDistanceThreshold ?? 34;
    this.minForegroundRatio = opts.minForegroundRatio ?? 0.035;
  }

  /**
   * @param {ImageData} imageData
   * @returns {Promise<import('../types').FabricRegionMetrics>}
   */
  async detectFromImageData(imageData) {
    const { width: w, height: h, data } = imageData;
    const band = Math.max(2, Math.round(Math.min(w, h) * 0.045));

    let cr = 0;
    let cg = 0;
    let cb = 0;
    let cn = 0;
    const addPixel = (x, y) => {
      const i = (y * w + x) * 4;
      cr += data[i];
      cg += data[i + 1];
      cb += data[i + 2];
      cn++;
    };
    for (let y = 0; y < band; y++) {
      for (let x = 0; x < w; x++) addPixel(x, y);
    }
    for (let y = h - band; y < h; y++) {
      for (let x = 0; x < w; x++) addPixel(x, y);
    }
    for (let y = band; y < h - band; y++) {
      for (let x = 0; x < band; x++) addPixel(x, y);
      for (let x = w - band; x < w; x++) addPixel(x, y);
    }
    cr /= cn;
    cg /= cn;
    cb /= cn;

    const dist = (i) => {
      const r = data[i] - cr;
      const g = data[i + 1] - cg;
      const b = data[i + 2] - cb;
      return Math.sqrt(r * r + g * g + b * b);
    };

    let thr = this.colorDistanceThreshold;
    let mask = new Uint8Array(w * h);
    const buildMask = (t) => {
      mask = new Uint8Array(w * h);
      let fg = 0;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4;
          if (dist(i) > t) {
            mask[y * w + x] = 1;
            fg++;
          }
        }
      }
      return fg;
    };

    let fgCount = buildMask(thr);
    let fgRatio = fgCount / (w * h);
    if (fgRatio < this.minForegroundRatio) {
      thr *= 0.62;
      fgCount = buildMask(thr);
      fgRatio = fgCount / (w * h);
    }

    /** Degenerate: almost everything foreground — heuristic is not separating subject */
    if (fgRatio > 0.86 || fgRatio < this.minForegroundRatio * 0.45) {
      return this._fallbackNeutralSwatch(w, h);
    }

    const xs = [];
    const ys = [];
    let sumX = 0;
    let sumY = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (!mask[y * w + x]) continue;
        xs.push(x);
        ys.push(y);
        sumX += x;
        sumY += y;
      }
    }
    const count = xs.length;
    if (count < 80) {
      return this._fallbackNeutralSwatch(w, h);
    }

    xs.sort((a, b) => a - b);
    ys.sort((a, b) => a - b);
    const q = (arr, p) => arr[Math.min(arr.length - 1, Math.max(0, Math.floor((p / 100) * (arr.length - 1))))];
    const minX = q(xs, 6);
    const maxX = q(xs, 94);
    const minY = q(ys, 6);
    const maxY = q(ys, 94);

    const bw = Math.max(1, maxX - minX + 1);
    const bh = Math.max(1, maxY - minY + 1);
    const bboxAreaRatio = (bw * bh) / (w * h);
    /** Spec: “% of frame occupied by fabric” — use actual fg pixel share for distance / zoom */
    const coverageRatio = fgRatio;
    const centroidX = sumX / count / w;
    const centroidY = sumY / count / h;

    const spreadOk = bboxAreaRatio > 0.06 && bboxAreaRatio < 0.97;
    const fgReasonable = fgRatio > 0.06 && fgRatio < 0.88;
    const confidence =
      Math.min(1, fgRatio * 3.2) *
      (spreadOk && fgReasonable ? 1 : 0.45) *
      (Math.abs(fgRatio - bboxAreaRatio) < 0.55 ? 1 : 0.75);

    return {
      coverageRatio,
      bboxX: minX / w,
      bboxY: minY / h,
      bboxW: bw / w,
      bboxH: bh / h,
      centroidX,
      centroidY,
      confidence: Math.min(0.98, Math.max(0.2, confidence)),
    };
  }

  /**
   * Lands in the “adjust / almost good” band so live UI stays usable; low confidence.
   * @param {number} w
   * @param {number} h
   * @returns {import('../types').FabricRegionMetrics}
   */
  _fallbackNeutralSwatch(w, h) {
    const targetCoverage = 0.58;
    const s = Math.sqrt(targetCoverage);
    const cw = w * s;
    const ch = h * s;
    const x0 = (w - cw) / 2;
    const y0 = (h - ch) / 2;
    const margin = 0.06;
    return {
      coverageRatio: targetCoverage,
      bboxX: margin,
      bboxY: margin,
      bboxW: 1 - 2 * margin,
      bboxH: 1 - 2 * margin,
      centroidX: 0.5,
      centroidY: 0.5,
      confidence: 0.22,
    };
  }
}
